import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Client } from '../types';
import { clientApi } from '../services/api';

export const useClientStore = defineStore('clients', () => {
  const clients = ref<Client[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  async function fetchClients() {
    isLoading.value = true;
    error.value = null;
    try {
      clients.value = await clientApi.getClients();
    } catch (e) {
      error.value = 'Failed to fetch clients.';
    } finally {
      isLoading.value = false;
    }
  }

  async function addClient(client: Omit<Client, 'id'>) {
    isLoading.value = true;
    error.value = null;
    try {
      const newClient = await clientApi.addClient(client);
      clients.value.push(newClient);
    } catch (e) {
      error.value = 'Failed to add client.';
    } finally {
      isLoading.value = false;
    }
  }

  async function updateClient(updatedClient: Client) {
    isLoading.value = true;
    error.value = null;
    try {
      const returnedClient = await clientApi.updateClient(updatedClient);
      const index = clients.value.findIndex(c => c.id === returnedClient.id);
      if (index !== -1) {
        clients.value[index] = returnedClient;
      }
    } catch (e) {
      error.value = 'Failed to update client.';
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteClient(id: string) {
    isLoading.value = true;
    error.value = null;
    try {
      await clientApi.deleteClient(id);
      clients.value = clients.value.filter(c => c.id !== id);
    } catch (e) {
      error.value = 'Failed to delete client.';
    } finally {
      isLoading.value = false;
    }
  }

  function getClient(clientId: string) {
    return clients.value.find(c => c.id === clientId);
  }

  return {
    clients,
    isLoading,
    error,
    fetchClients,
    addClient,
    updateClient,
    deleteClient,
    getClient
  };
});
